export class FaceDetectionService {
  private static API_BASE_URL = "https://faceapp.cditproject.org"

  static async detectFaces(
    imageBase64: string,
    punchCode: string,
    latitude?: number,
    longitude?: number,
  ): Promise<any> {
    let backendResponse = null

    try {
      console.log("FaceDetectionService: Starting real face detection with backend...")

      if (!imageBase64) {
        throw new Error("No image data provided")
      }

      if (!punchCode || punchCode.trim() === "") {
        throw new Error("Punch code is required")
      }

      console.log("FaceDetectionService: Image data length:", imageBase64.length)
      console.log("FaceDetectionService: Location data:", { latitude, longitude })
      console.log("FaceDetectionService: Punch code:", punchCode)

      // Create FormData for web
      const formData = new FormData()

      // For web, we need to convert base64 to blob
      const base64Response = await fetch(`data:image/jpeg;base64,${imageBase64}`)
      const blob = await base64Response.blob()

      formData.append("captured_image", blob, "face_capture.jpg")
      formData.append("punch_code", punchCode.trim())
      formData.append("device_type", "1")

      if (latitude !== undefined) {
        formData.append("latitude", latitude.toString())
      }
      if (longitude !== undefined) {
        formData.append("longitude", longitude.toString())
      }

      // Add timestamp
      formData.append("timestamp", new Date().toISOString())

      console.log("FaceDetectionService: Sending FormData request to backend with punch code...")

      // Make API call to your backend with FormData
      const apiResponse = await fetch(`${this.API_BASE_URL}/api/verify-candidate`, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
          // Note: Don't set Content-Type for FormData, let browser set it with boundary
        },
      })

      console.log("FaceDetectionService: Backend response status:", apiResponse.status)

      if (!apiResponse.ok) {
        const errorText = await apiResponse.text()
        console.error("FaceDetectionService: Backend error response:", errorText)

        // Try to parse error as JSON to get detailed message
        let errorMessage = `Backend API error: ${apiResponse.status}`
        let fullErrorResponse = null

        try {
          const errorJson = JSON.parse(errorText)
          errorMessage = errorJson.message || errorMessage
          fullErrorResponse = errorJson
        } catch (parseError) {
          errorMessage = errorText || errorMessage
        }

        backendResponse = {
          status: "failed",
          message: errorMessage,
          rawResponse: errorText,
          fullResponse: fullErrorResponse,
        }

        // Return error response structure instead of throwing
        return {
          faces: [],
          backendResponse: backendResponse,
          success: false,
        }
      }

      const result = await apiResponse.json()
      console.log("FaceDetectionService: Backend response:", result)

      // Add success response structure
      backendResponse = {
        status: result.status || "success",
        message: result.message || "Verification completed successfully",
        data: result,
        fullResponse: result,
      }

      // Transform backend response to match our expected format
      const faces = this.transformBackendResponse(result, punchCode)
      console.log(`FaceDetectionService: Transformed ${faces.length} faces from backend response`)

      // Return both faces and backend response
      return {
        faces: faces,
        backendResponse: backendResponse,
        success: true,
      }
    } catch (error) {
      console.error("FaceDetectionService: Error detecting faces:", error)

      // Return error structure instead of throwing
      return {
        faces: [],
        backendResponse: {
          status: "failed",
          message: error instanceof Error ? error.message : "Network error occurred",
          error: error,
        },
        success: false,
      }
    }
  }

  static async markExit(punchCode: string, latitude: number, longitude: number): Promise<any> {
    try {
      console.log("FaceDetectionService: Marking exit with backend...")

      if (!punchCode || punchCode.trim() === "") {
        throw new Error("Punch code is required")
      }

      // For exit, create FormData without image or with empty image
      const formData = new FormData()
      formData.append("punch_code", punchCode.trim())
      formData.append("device_type", "1")
      formData.append("latitude", latitude.toString())
      formData.append("longitude", longitude.toString())
      formData.append("timestamp", new Date().toISOString())
      formData.append("type", "exit")

      // Create a minimal base64 image for exit if backend requires it
      const emptyImageBase64 =
        "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="

      // Convert base64 to blob for web
      const base64Response = await fetch(`data:image/png;base64,${emptyImageBase64}`)
      const blob = await base64Response.blob()

      formData.append("captured_image", blob, "exit_placeholder.png")

      console.log("FaceDetectionService: Sending exit request to backend with punch code:", punchCode)

      // You might need a different endpoint for exit, but using the same for now
      const apiResponse = await fetch(`${this.API_BASE_URL}/api/verify-candidate`, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
          // Note: Don't set Content-Type for FormData, let browser set it with boundary
        },
      })

      console.log("FaceDetectionService: Exit response status:", apiResponse.status)

      if (!apiResponse.ok) {
        const errorText = await apiResponse.text()
        console.error("FaceDetectionService: Exit error response:", errorText)

        let backendError = {
          status: "failed",
          message: `Backend API error: ${apiResponse.status}`,
          rawResponse: errorText,
        }

        try {
          const errorJson = JSON.parse(errorText)
          backendError = {
            status: errorJson.status || "failed",
            message: errorJson.message || `Backend API error: ${apiResponse.status}`,
            rawResponse: errorText,
          }
        } catch (parseError) {
          // Keep the default error structure
        }

        throw backendError
      }

      const result = await apiResponse.json()
      console.log("FaceDetectionService: Exit response:", result)

      // Return structured response
      return {
        status: result.status || "success",
        message: result.message || "Exit marked successfully",
        data: result,
        fullResponse: result,
      }
    } catch (error) {
      console.error("FaceDetectionService: Error marking exit:", error)
      throw error
    }
  }

  static async applyDutyPass(
    punchCode: string,
    imageBlob: Blob,
    purpose: string,
    location: { latitude: number; longitude: number; address: string }
  ): Promise<any> {
    try {
      console.log("FaceDetectionService: Applying duty pass with backend...")

      if (!punchCode || punchCode.trim() === "") {
        throw new Error("Punch code is required")
      }

      const formData = new FormData()
      formData.append("punch_code", punchCode.trim())
      formData.append("captured_image", imageBlob, "duty_pass_capture.jpg")
      formData.append("purpose", purpose.trim())
      formData.append("address", location.address)
      formData.append("latitude", location.latitude.toString())
      formData.append("longitude", location.longitude.toString())
      formData.append("device_type", "1")

      console.log("FaceDetectionService: Sending duty pass request to backend...")

      const apiResponse = await fetch(`${this.API_BASE_URL}/api/duty-pass/apply`, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      })

      console.log("FaceDetectionService: Duty pass response status:", apiResponse.status)

      if (!apiResponse.ok) {
        const errorText = await apiResponse.text()
        console.error("FaceDetectionService: Duty pass error response:", errorText)

        let errorMessage = `Backend API error: ${apiResponse.status}`
        try {
          const errorJson = JSON.parse(errorText)
          errorMessage = errorJson.message || errorMessage
        } catch (e) {}

        throw new Error(errorMessage)
      }

      const result = await apiResponse.json()
      console.log("FaceDetectionService: Duty pass success response:", result)

      return result
    } catch (error) {
      console.error("FaceDetectionService: Error applying duty pass:", error)
      throw error
    }
  }

  private static transformBackendResponse(backendResponse: any, punchCode: string): any[] {
    try {
      // Transform your backend response to match the expected face format
      // Adjust this based on your actual backend response structure

      // Check if verification was successful
      if (backendResponse.success || backendResponse.verified) {
        // If candidate is verified, return face data
        const faceData = {
          id: "verified_face_1",
          bounds: {
            origin: { x: 150, y: 200 },
            size: { width: 200, height: 200 },
          },
          confidence: backendResponse.confidence || 0.95,
          faceID: backendResponse.candidate_id || backendResponse.person_id || `verified_${Date.now()}`,
          timestamp: new Date().toISOString(),
          punchCode: punchCode,
          verified: true,
          backendData: backendResponse,
        }

        return [faceData]
      }

      // If faces array is provided
      if (backendResponse.faces && Array.isArray(backendResponse.faces)) {
        return backendResponse.faces.map((face: any, index: number) => ({
          id: face.id || `face_${index}`,
          bounds: {
            origin: {
              x: face.x || face.left || 150,
              y: face.y || face.top || 200,
            },
            size: {
              width: face.width || 200,
              height: face.height || 200,
            },
          },
          confidence: face.confidence || 0.95,
          faceID: face.face_id || face.person_id || `person_${Date.now()}_${index}`,
          timestamp: new Date().toISOString(),
          punchCode: punchCode,
          verified: face.verified || false,
          backendData: face,
        }))
      }

      // If candidate data is provided
      if (backendResponse.candidate) {
        return [
          {
            id: "candidate_face_1",
            bounds: {
              origin: { x: 150, y: 200 },
              size: { width: 200, height: 200 },
            },
            confidence: 0.95,
            faceID: backendResponse.candidate.id || `candidate_${Date.now()}`,
            timestamp: new Date().toISOString(),
            punchCode: punchCode,
            verified: true,
            candidateInfo: backendResponse.candidate,
            backendData: backendResponse,
          },
        ]
      }

      // If no faces detected but response is successful
      if (backendResponse.message && backendResponse.message.includes("No face")) {
        console.log("FaceDetectionService: Backend detected no faces")
        return []
      }

      // Default case - if response seems successful but format is different
      if (backendResponse.status === "success" || backendResponse.success === true) {
        return [
          {
            id: "detected_face_1",
            bounds: {
              origin: { x: 150, y: 200 },
              size: { width: 200, height: 200 },
            },
            confidence: 0.95,
            faceID: `person_${Date.now()}`,
            timestamp: new Date().toISOString(),
            punchCode: punchCode,
            verified: true,
            backendData: backendResponse,
          },
        ]
      }

      return []
    } catch (error) {
      console.error("FaceDetectionService: Error transforming backend response:", error)
      return []
    }
  }

  static async recognizeFace(faceData: any): Promise<string | null> {
    try {
      console.log("FaceDetectionService: Starting face recognition...")

      if (!faceData) {
        throw new Error("No face data provided")
      }

      // Use backend data if available
      if (faceData.backendData) {
        if (faceData.backendData.candidate_id) {
          return faceData.backendData.candidate_id
        }
        if (faceData.backendData.person_id) {
          return faceData.backendData.person_id
        }
        if (faceData.candidateInfo && faceData.candidateInfo.id) {
          return faceData.candidateInfo.id
        }
      }

      return faceData.faceID || null
    } catch (error) {
      console.error("FaceDetectionService: Error recognizing face:", error)
      return null
    }
  }
}

export default FaceDetectionService 